/* Performance batch 2 (B2, Oct 2026): opening a staff-portal address directly (a link, a bookmark, the phone icon).

   Before, the steps ran one after another: the app's code, then the portal frame's code, then the identity
   request (/api/crm/ws/me), then the page guard's code, then the page's code, then the page's data.
   Here the identity request and the CODE files of the portal frame, the page guard and the opened page start
   together, as soon as the first script runs. Nothing is shown earlier and nothing private is kept:
   - only program files (the same for everybody) are fetched ahead; no page data is fetched ahead;
   - the identity answer is handed only to the portal frame of this same tab, once, and only if the sign-in is
     still the one it was asked with (wsKit.js checks the token); otherwise it is dropped and asked again;
   - every page still waits for the identity check exactly as before, and the server checks every request.
   Only dynamic import() calls are listed here, so none of this code is added to the public pages. */
import { backendBase } from '@/native/env';

const PAGES = [
  // [address pattern under /ws, the page's code]
  [/^\/ws\/?$/, () => import('@/pages/ws/MyDay')],
  [/^\/ws\/overview\/?$/, () => import('@/pages/ws/Overview')],
  [/^\/ws\/work\/?$/, () => import('@/pages/ws/Work')],
  [/^\/ws\/leads\/?$/, () => import('@/pages/ws/Leads')],
  [/^\/ws\/leads\/[^/]+\/?$/, () => import('@/pages/ws/LeadDetail')],
  [/^\/ws\/jobs\/?$/, () => import('@/pages/ws/Jobs')],
  [/^\/ws\/jobs\/[^/]+\/?$/, () => import('@/pages/ws/JobDetail')],
  [/^\/ws\/tasks\/?$/, () => import('@/pages/ws/Tasks')],
  [/^\/ws\/team\/?$/, () => import('@/pages/ws/Team')],
  [/^\/ws\/approvals\/?$/, () => import('@/pages/ws/Approvals')],
  [/^\/ws\/reports\/?$/, () => import('@/pages/ws/Reports')],
  [/^\/ws\/notifications\/?$/, () => import('@/pages/ws/Notifications')],
  [/^\/ws\/work-reports\/?$/, () => import('@/pages/admin/WorkReports')],
  [/^\/ws\/employees\/?$/, () => import('@/pages/admin/Employees')],
  [/^\/ws\/crm\/[^/]+\/?$/, () => import('@/pages/crm/CrmEnquiryDetail')],
];

export function isPortalPath(pathname) {
  return pathname === '/ws' || pathname.startsWith('/ws/');
}

export function startPortalEarly(pathname) {
  let token = null;
  let staff = false;
  try {
    token = localStorage.getItem('aquaruby_token');
    // only for a sign-in the app itself would let into the portal (ProtectedRoute: employee, admin, super_admin)
    const u = JSON.parse(localStorage.getItem('aquaruby_user') || 'null') || {};
    staff = ['employee', 'admin', 'super_admin'].some((r) => u.role === r || (Array.isArray(u.extra_roles) && u.extra_roles.includes(r)));
  } catch { /* storage blocked or unreadable: the normal order applies */ }
  if (!token || !staff) return;                         // not signed in, or not staff: as before, nothing is asked
  try {
    const base = backendBase(process.env.REACT_APP_BACKEND_URL || '');
    if (base === null) throw new Error('app starting');  // Android staff app before start-up: the portal asks normally
    const me = fetch(`${base}/api/crm/ws/me`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));
    me.catch(() => {});                                 // a refusal is handled by the normal request (see wsKit.js)
    window.__wsEarly = { token, me, at: Date.now() };
  } catch { /* the portal asks normally */ }
  const quiet = (p) => { try { p().catch(() => {}); } catch { /* the page loads its code normally */ } };
  quiet(() => import('@/pages/ws/WsLayout'));
  quiet(() => import('@/pages/ws/PortalGuards'));
  const page = PAGES.find(([re]) => re.test(pathname));
  if (page) quiet(page[1]);
}
